import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, GripVertical, X } from "lucide-react";
import AdminShell from "@/components/admin/AdminShell";
import { adminApi } from "@/lib/adminApi";

/** Content Calendar — planning-only. Dragging a draft onto a day sets a
 * planned-date LABEL; nothing is ever auto-published. */

const KIND_STYLE = {
  published: "border-emerald-400/50 text-emerald-300 bg-emerald-400/5",
  planned: "border-gold/50 text-gold bg-gold/5",
  awaiting: "border-amber-400/50 text-amber-300 bg-amber-400/5",
  draft: "border-sky-400/50 text-sky-300 bg-sky-400/5",
};

const monthLabel = (m) =>
  new Date(`${m}-01T12:00:00Z`).toLocaleDateString(undefined, { month: "long", year: "numeric" });

function shiftMonth(m, delta) {
  const [y, mo] = m.split("-").map(Number);
  const d = new Date(Date.UTC(y, mo - 1 + delta, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
}

function monthDays(m) {
  const [y, mo] = m.split("-").map(Number);
  const first = new Date(Date.UTC(y, mo - 1, 1));
  const daysIn = new Date(Date.UTC(y, mo, 0)).getUTCDate();
  const lead = (first.getUTCDay() + 6) % 7; // Monday-first
  const cells = Array(lead).fill(null);
  for (let d = 1; d <= daysIn; d++) cells.push(`${m}-${String(d).padStart(2, "0")}`);
  return cells;
}

function Chip({ item, draggable, onClear }) {
  return (
    <div
      draggable={draggable}
      onDragStart={(e) => e.dataTransfer.setData("text/article-id", item.id)}
      data-testid={`calendar-chip-${item.slug}`}
      className={`group flex items-center gap-1 px-1.5 py-1 mb-1 border text-[10px] leading-tight ${KIND_STYLE[item.kind] || "border-white/15 text-bone/60"} ${draggable ? "cursor-grab active:cursor-grabbing" : ""}`}
      title={`${item.reference || ""} ${item.title}`}
    >
      {draggable && <GripVertical size={9} className="shrink-0 opacity-50" />}
      <Link to={`/admin/journal?edit=${encodeURIComponent(item.slug)}`} className="truncate hover:underline">
        {item.reference ? `${item.reference} · ` : ""}{item.title}
      </Link>
      {item.kind === "planned" && onClear && (
        <button onClick={() => onClear(item)} className="ml-auto opacity-0 group-hover:opacity-100 shrink-0" title="Remove planned date" data-testid={`calendar-unplan-${item.slug}`}>
          <X size={10} />
        </button>
      )}
    </div>
  );
}

export default function AdminContentCalendar() {
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [dragOver, setDragOver] = useState("");

  const load = useCallback(async () => {
    setErr("");
    try { setData(await adminApi.journalCalendar(month)); }
    catch (e) { setErr(e.message); }
  }, [month]);
  useEffect(() => { load(); }, [load]);

  const plan = async (articleId, date) => {
    try { await adminApi.planArticle(articleId, date); await load(); }
    catch (e) { setErr(e.message); }
  };

  const onDrop = (e, date) => {
    e.preventDefault();
    setDragOver("");
    const id = e.dataTransfer.getData("text/article-id");
    if (id) plan(id, date);
  };

  const byDate = {};
  (data?.items || []).forEach((it) => { (byDate[it.date] = byDate[it.date] || []).push(it); });
  const today = new Date().toISOString().slice(0, 10);

  return (
    <AdminShell title="Content Calendar">
      <p className="text-sm text-bone/60 -mt-6 mb-6 max-w-3xl">
        Planning-only: drag a draft onto a day to set its planned date. Publishing always stays a
        manual EN + NL approval action — nothing on this calendar publishes anything.
      </p>

      <div className="flex items-center gap-4 mb-6" data-testid="calendar-header">
        <button onClick={() => setMonth((m) => shiftMonth(m, -1))} data-testid="calendar-prev"
          className="p-2 border border-white/15 text-bone/60 hover:border-gold/40 hover:text-gold"><ChevronLeft size={14} /></button>
        <h2 className="font-serif text-xl text-bone capitalize min-w-[180px] text-center" data-testid="calendar-month-label">{monthLabel(month)}</h2>
        <button onClick={() => setMonth((m) => shiftMonth(m, 1))} data-testid="calendar-next"
          className="p-2 border border-white/15 text-bone/60 hover:border-gold/40 hover:text-gold"><ChevronRight size={14} /></button>
        <div className="hidden sm:flex flex-wrap gap-2 ml-auto text-[9px] tracking-[0.18em] uppercase">
          {Object.entries({ published: "Published", planned: "Planned", awaiting: "Awaiting approval", draft: "Draft" }).map(([k, l]) => (
            <span key={k} className={`px-2 py-0.5 border ${KIND_STYLE[k]}`}>{l}</span>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_280px] gap-6">
        {/* Month grid */}
        <div className="overflow-x-auto">
          <div className="min-w-[760px]">
            <div className="grid grid-cols-7 border-l border-t border-white/10">
              {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => (
                <div key={d} className="px-2 py-2 border-r border-b border-white/10 text-[9px] tracking-[0.22em] uppercase text-bone/40">{d}</div>
              ))}
              {monthDays(month).map((date, i) => (
                <div key={i}
                  onDragOver={date ? (e) => { e.preventDefault(); setDragOver(date); } : undefined}
                  onDragLeave={() => setDragOver("")}
                  onDrop={date ? (e) => onDrop(e, date) : undefined}
                  data-testid={date ? `calendar-day-${date}` : undefined}
                  className={`min-h-[92px] px-1.5 py-1.5 border-r border-b border-white/10 transition-colors ${
                    !date ? "bg-white/[0.01]" : dragOver === date ? "bg-gold/10" : "bg-white/[0.02]"
                  }`}>
                  {date && (
                    <>
                      <p className={`text-[10px] font-mono mb-1 ${date === today ? "text-gold" : "text-bone/35"}`}>{Number(date.slice(8))}</p>
                      {(byDate[date] || []).map((it) => (
                        <Chip key={it.id} item={it} draggable={it.kind !== "published"}
                          onClear={(x) => plan(x.id, null)} />
                      ))}
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Unplanned drafts */}
        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver("unplanned"); }}
          onDragLeave={() => setDragOver("")}
          onDrop={(e) => { e.preventDefault(); setDragOver(""); const id = e.dataTransfer.getData("text/article-id"); if (id) plan(id, null); }}
          className={`border p-4 h-fit ${dragOver === "unplanned" ? "border-gold/60 bg-gold/5" : "border-white/10 bg-white/[0.02]"}`}
          data-testid="calendar-unplanned">
          <p className="text-[10px] tracking-[0.28em] uppercase text-gold/70 mb-3">
            Unplanned · {(data?.unplanned || []).length}
          </p>
          <p className="text-[11px] text-bone/40 mb-3">Drag onto a day to plan. Drop back here to unplan.</p>
          {(data?.unplanned || []).map((it) => <Chip key={it.id} item={it} draggable />)}
          {(data?.unplanned || []).length === 0 && <p className="text-[12px] text-bone/40">Everything is planned.</p>}
        </div>
      </div>

      {err && <p className="mt-4 text-sm text-red-400" data-testid="calendar-error">{err}</p>}
    </AdminShell>
  );
}
